import React, { CSSProperties } from 'react';

import ProgressBadge from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/GoalProgressCircle/ProgressBadge';
import {
  calculatePathOffset,
  useSVGPathLength,
  SVG_VIEWBOX_HEIGHT,
  SVG_VIEWBOX_WIDTH,
} from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/GoalProgressCircle/utils/svgAnimationUtils';

import styles from './MonetaryProgressCircle.module.scss';

interface MonetaryProgressCircleProps {
  percentage: number;
}

const PATH_OUTLINE =
  'M23.8162 99.1838C16.2642 91.6317 11.1212 82.0099 9.03759 71.5349C6.95399 61.0599 8.02337 50.2023 12.1105 40.3351C16.1976 30.4679 23.119 22.0342 31.9992 16.1006C40.8795 10.167 51.3198 7 62 7C72.6802 7 83.1205 10.167 92.0008 16.1006C100.881 22.0342 107.802 30.4679 111.889 40.3351C115.977 50.2023 117.046 61.0599 114.962 71.5349C112.879 82.0099 107.736 91.6317 100.184 99.1838L99.8019 98.8019C107.278 91.3254 112.37 81.7998 114.433 71.4295C116.496 61.0593 115.437 50.3103 111.391 40.5417C107.344 30.7732 100.492 22.4239 91.7008 16.5496C82.9093 10.6754 72.5734 7.54 62 7.54C51.4266 7.54 41.0907 10.6754 32.2992 16.5496C23.5078 22.4239 16.6557 30.7732 12.6094 40.5417C8.56314 50.3103 7.50445 61.0593 9.56722 71.4295C11.63 81.7998 16.7216 91.3254 24.1981 98.8019L23.8162 99.1838Z';

const getFillColor = (percentage: number): string => {
  if (percentage === 100) {
    return 'var(--progress-purple)';
  }
  if (percentage >= 55) {
    return 'var(--progress-green)';
  }
  if (percentage >= 25) {
    return 'var(--progress-orange)';
  }
  return 'var(--progress-blue)';
};

const getBadgeColor = (percentage: number): string => {
  if (percentage === 100) {
    return 'var(--badge-purple)';
  }
  return getFillColor(percentage);
};

const MonetaryProgressCircle: React.FC<MonetaryProgressCircleProps> = ({ percentage }) => {
  const fillColor = getFillColor(percentage);
  const badgeColor = getBadgeColor(percentage);
  const { pathRefs, pathLengths } = useSVGPathLength(1);

  const offset = calculatePathOffset({
    pathLength: pathLengths[0] || 0,
    fillPercentage: percentage,
  });

  return (
    <svg
      width={SVG_VIEWBOX_WIDTH}
      height={SVG_VIEWBOX_HEIGHT}
      viewBox={`0 0 ${SVG_VIEWBOX_WIDTH} ${SVG_VIEWBOX_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* outline */}
      <g filter="url(#filter0_d_1241_10144)">
        <path
          d={PATH_OUTLINE}
          strokeWidth="12"
          strokeLinejoin="round"
          shapeRendering="crispEdges"
          className={styles.outline}
        />
      </g>
      {/* fill */}
      <path
        ref={(el) => {
          pathRefs.current[0] = el;
        }}
        d={PATH_OUTLINE}
        className={styles.fill}
        style={{ '--fill-color': fillColor } as CSSProperties}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={pathLengths[0] || 0}
        strokeDashoffset={offset || 0}
      />
      <ProgressBadge badgeColor={badgeColor} />
      <defs>
        <filter
          id="filter0_d_1241_10144"
          x="0"
          y="0"
          width={SVG_VIEWBOX_WIDTH}
          height={SVG_VIEWBOX_HEIGHT}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />

          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1241_10144" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1241_10144" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

export default MonetaryProgressCircle;
